import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Landing from "@/pages/Landing";
import { getToken, clearToken, fetchMe, refreshSession } from "@/lib/mastroAuth";

// 06.9-D — Persistent Session Root Entry
// If a valid authenticated/persistent session already exists, entering `/`
// enters `/app` automatically. Otherwise the landing is shown unchanged.
// Reuses the SAME auth primitives used by DashboardLayout — no duplicate
// auth architecture, no new endpoint, no storage/TTL/cookie changes.
// Redirect is decided ONLY after validity is confirmed via fetchMe or
// refreshSession, never on mere token-string presence. At most one
// /auth/refresh per mount (loop-safe, consistent with 06.9-B).
export default function SmartRoot() {
  // "checking" | "landing" | "app"
  const [state, setState] = useState("checking");

  useEffect(() => {
    let active = true;
    (async () => {
      // 1) If an in-tab access token is present, verify it with /auth/me.
      //    Never redirect on mere token presence — only after fetchMe succeeds.
      let token = getToken();
      if (token) {
        try {
          await fetchMe(token);
          if (!active) return;
          setState("app");
          return;
        } catch (_) {
          // Stale/invalid access token: drop it and fall through to the
          // silent persistent-session recovery below.
          clearToken();
        }
      }

      // 2) Silent persistent-session recovery via the httpOnly refresh cookie.
      //    Single attempt per mount — never loop. refreshSession() writes the
      //    new access token into sessionStorage on success (unchanged).
      try {
        const data = await refreshSession();
        if (!active) return;
        if (data?.access_token) {
          setState("app");
          return;
        }
      } catch (_) {
        // No valid persistent session — fall through to landing.
      }

      if (!active) return;
      setState("landing");
    })();
    return () => {
      active = false;
    };
  }, []);

  if (state === "checking") {
    // Neutral placeholder while validity is being established. Kept minimal
    // to avoid a visible bounce between landing and /app.
    return (
      <div
        data-testid="root-checking"
        className="min-h-screen w-full bg-white"
      />
    );
  }

  if (state === "app") {
    return <Navigate to="/app" replace />;
  }

  return <Landing />;
}
